import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Public Pickup QR landing page — no auth required. */
export default function PickupQrLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/pickup-qr/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "QR not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <p className="text-slate-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-sm w-full text-center">
          <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900">QR not available</h1>
          <p className="text-sm text-slate-500 mt-2">{error || "The QR may have been regenerated. Please request a new copy from the studio."}</p>
        </div>
      </div>
    );
  }

  const imgUrl = `${API}/public/pickup-qr/${token}/image`;
  const downloadName = `${(data.student_name || "Student").replace(/\s+/g, "_")}_${data.display_id || token}_QR.png`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white px-4 py-6 sm:py-12">
      <div className="max-w-sm mx-auto bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 px-6 py-5 text-white text-center">
          <p className="text-[10px] uppercase tracking-widest opacity-80">Student Pickup Authorization</p>
          <h1 className="text-lg font-bold mt-1" data-testid="public-qr-org">{data.organization_name || data.studio_name || "Studio"}</h1>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 grid place-items-center">
            <img
              src={imgUrl}
              alt={`Pickup QR for ${data.student_name}`}
              className="w-64 h-64"
              data-testid="public-qr-image"
            />
          </div>

          <div className="text-center space-y-1">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Student Name</p>
            <p className="text-xl font-bold text-slate-900" data-testid="public-qr-student-name">{data.student_name}</p>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-2">Student ID</p>
            <p className="text-base font-bold text-violet-700" data-testid="public-qr-display-id">{data.display_id || "—"}</p>
          </div>

          <a
            href={imgUrl}
            download={downloadName}
            className="block text-center rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold py-2.5"
            data-testid="public-qr-download"
          >
            <Download className="inline w-4 h-4 mr-1" /> Download QR
          </a>

          <p className="text-[11px] text-slate-500 text-center pt-2">
            Please show this QR code at <strong>{data.studio_name}</strong> during student pickup.
          </p>
        </div>
      </div>
    </div>
  );
}
